/**
 * pages/Settings.tsx - User Settings Page
 * Manages user preferences and account settings
 * Wired to backend GET/PUT /api/users/settings
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '@/lib/api/users';
import { UserSettings } from '@/types/user';
import SEO from '@/components/common/SEO';
import {
  Settings as SettingsIcon,
  Bell,
  MessageSquare,
  Lock,
  Globe,
  Eye,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';

interface SettingsState extends UserSettings {
  // Local-only fields (not persisted to backend)
  emailDigest: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'NEVER';
}

const SettingsPage: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState<SettingsState>({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    jobMatchAlerts: true,
    applicationUpdates: true,
    weeklyJobDigest: true,
    communityReplies: true,
    profileVisibility: 'PUBLIC',
    showContactInfo: true,
    resumeVisibility: false,
    allowMessages: true,
    jobSearchStatus: 'OPEN',
    twoFactorEnabled: false,
    sessionTimeout: '30m',
    loginAlerts: true,
    language: 'en',
    timezone: 'Asia/Kolkata',
    emailDigest: 'WEEKLY',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'notifications' | 'privacy' | 'preferences'>('notifications');

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Load settings from backend API on mount, fall back to localStorage
  useEffect(() => {
    if (!isAuthenticated) return;

    usersAPI.getSettings()
      .then((backendSettings: UserSettings) => {
        // Merge backend settings with local-only fields
        const emailDigest = (localStorage.getItem('emailDigest') as SettingsState['emailDigest']) || 'WEEKLY';
        setSettings(prev => ({
          ...prev,
          ...backendSettings,
          emailDigest,
        }));
      })
      .catch((err) => {
        console.error('Error loading settings from backend:', err);
        // Fallback to localStorage
        const saved = localStorage.getItem('userSettings');
        if (saved) {
          try {
            setSettings(prev => ({ ...prev, ...JSON.parse(saved) }));
          } catch (e) {
            console.error('Error loading from localStorage:', e);
          }
        }
      });
  }, [isAuthenticated]);

  const handleSettingChange = (key: keyof SettingsState, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSuccess(false);
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Extract local-only fields before sending to backend
      const { emailDigest, ...backendFields } = settings;
      await usersAPI.updateSettings(backendFields);

      // Persist local-only fields to localStorage
      localStorage.setItem('emailDigest', emailDigest);

      // Also save merged copy to localStorage as fallback
      localStorage.setItem('userSettings', JSON.stringify(settings));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      await logout();
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      <SEO title="Settings" noindex />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <SettingsIcon className="h-8 w-8 text-primary" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-2">Manage your preferences and account settings</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 rounded-xl border border-success/30 bg-success/5 p-4 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
            <p className="text-sm text-success font-medium">Settings saved successfully</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="rounded-lg border border-border bg-card p-2 sticky top-24">
              {[
                { id: 'notifications', label: 'Notifications', icon: Bell },
                { id: 'privacy', label: 'Privacy & Safety', icon: Lock },
                { id: 'preferences', label: 'Preferences', icon: Globe },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id as any)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${
                    activeTab === item.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-secondary'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-3">
            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
                <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  Notification Settings
                </h2>

                <div className="space-y-6">
                  {/* Email Notifications */}
                  <div className="flex items-center justify-between pb-6 border-b border-border">
                    <div>
                      <p className="font-semibold text-foreground">Email Notifications</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Receive updates about your listings and messages
                      </p>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications}
                        onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                        className="w-4 h-4 rounded border-2 border-border cursor-pointer"
                      />
                    </label>
                  </div>

                  {/* SMS Notifications */}
                  <div className="flex items-center justify-between pb-6 border-b border-border">
                    <div>
                      <p className="font-semibold text-foreground">SMS Notifications</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Get important updates via text message
                      </p>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.smsNotifications}
                        onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                        className="w-4 h-4 rounded border-2 border-border cursor-pointer"
                      />
                    </label>
                  </div>

                  {/* Push Notifications */}
                  <div className="flex items-center justify-between pb-6 border-b border-border">
                    <div>
                      <p className="font-semibold text-foreground">Push Notifications</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Browser notifications for real-time updates
                      </p>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.pushNotifications}
                        onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                        className="w-4 h-4 rounded border-2 border-border cursor-pointer"
                      />
                    </label>
                  </div>

                  {/* Email Digest */}
                  <div>
                    <label className="block font-semibold text-foreground mb-2">Email Digest</label>
                    <p className="text-sm text-muted-foreground mb-4">
                      How often would you like to receive summary emails?
                    </p>
                    <select
                      value={settings.weeklyJobDigest ? 'WEEKLY' : 'NEVER'}
                      onChange={(e) => handleSettingChange('weeklyJobDigest', e.target.value === 'WEEKLY')}
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                    >
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="NEVER">Never</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Tab */}
            {activeTab === 'privacy' && (
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
                <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                  <Lock className="h-5 w-5 text-primary" />
                  Privacy & Safety
                </h2>

                <div className="space-y-6">
                  {/* Profile Visibility */}
                  <div className="pb-6 border-b border-border">
                    <label className="block font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Eye className="h-4 w-4 text-primary" />
                      Profile Visibility
                    </label>
                    <p className="text-sm text-muted-foreground mb-4">
                      Control who can see your profile information
                    </p>
                    <div className="space-y-3">
                      {[
                        { value: 'PUBLIC', label: 'Public', desc: 'Everyone can see your profile' },
                        { value: 'PRIVATE', label: 'Private', desc: 'Only your contacts can see your profile' },
                      ].map(option => (
                        <label key={option.value} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-secondary cursor-pointer transition">
                          <input
                            type="radio"
                            name="privacy"
                            value={option.value}
                            checked={settings.profileVisibility === option.value}
                            onChange={(e) => handleSettingChange('profileVisibility', e.target.value)}
                            className="w-4 h-4"
                          />
                          <div>
                            <p className="font-medium text-foreground">{option.label}</p>
                            <p className="text-xs text-muted-foreground">{option.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
                <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-primary" />
                  Preferences
                </h2>

                <div className="space-y-6">
                  {/* Language */}
                  <div className="pb-6 border-b border-border">
                    <label className="block font-semibold text-foreground mb-2">Language</label>
                    <select
                      value={settings.language}
                      onChange={(e) => handleSettingChange('language', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                    >
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                      <option value="ta">Tamil</option>
                      <option value="te">Telugu</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                    </select>
                  </div>

                  {/* Timezone */}
                  <div className="pb-6 border-b border-border">
                    <label className="block font-semibold text-foreground mb-2">Timezone</label>
                    <select
                      value={settings.timezone}
                      onChange={(e) => handleSettingChange('timezone', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                    >
                      <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                      <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                      <option value="Asia/Bangkok">Asia/Bangkok (ICT)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>

                  {/* Danger Zone */}
                  <div className="pt-6 border-t-2 border-destructive/20">
                    <h3 className="font-semibold text-destructive mb-4">Account</h3>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 rounded-lg border border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10 transition font-medium"
                    >
                      <MessageSquare className="h-4 w-4 inline mr-2" />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition font-semibold disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save All Settings
              </>
            )}
          </button>
        </div>

        {/* Back Link */}
        <div className="mt-8 text-center">
          <a href="/profile" className="text-primary hover:underline font-medium">
            ← Back to Profile
          </a>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;